import type { UiState } from 'instantsearch.js';

import type { IAlgoliaRouteState, IUiState, SFCCFilterMap } from '@libs/algolia';
import { AlgoliaIndice } from '@libs/algolia';
import {
    getGaUserId,
    initAlgoliaTracking,
    isOptedOutTracking,
    waitForInitialSearchUserTokenAfterConsent,
} from '@libs/analytics';

import type { Facets } from '@/types/facetedNavigation';

import {
    type AlgoliaRequestWithParams,
    addUrlRefinementsToRequest,
    type UrlRefinement,
} from './urlRefinementRequestUtils';
import { getStateFromRoute } from './utils';

export type { AlgoliaRequestWithParams, UrlRefinement } from './urlRefinementRequestUtils';

const GOOGLE_ANALYTICS_TOKEN_POLL_INTERVAL_MS = 100;
const INITIAL_SEARCH_USER_TOKEN_MAX_WAIT_MS = 5000;
const INVALID_USER_TOKEN_VALUES = new Set(['anonymous', 'null', 'undefined']);

// A token is "real" only when it's present and not an InstantSearch-generated
// anonymous fallback (`anonymous-*`).
export const hasValidUserToken = (userToken: unknown): userToken is string => {
    if (typeof userToken !== 'string') {
        return false;
    }

    const normalizedUserToken = userToken.trim().toLowerCase();
    return (
        normalizedUserToken.length > 0 &&
        !INVALID_USER_TOKEN_VALUES.has(normalizedUserToken) &&
        !normalizedUserToken.startsWith('anonymous-')
    );
};

export const getValidUserTokenFromRequests = (requests: readonly AlgoliaRequestWithParams[]): string | undefined =>
    requests.find(request => hasValidUserToken(request.params?.userToken))?.params?.userToken as string | undefined;

const getValidGoogleAnalyticsUserToken = (): string | undefined => {
    const userToken = getGaUserId('_ga');
    return hasValidUserToken(userToken) ? userToken : undefined;
};

const getBestInitialSearchUserToken = (getRequestUserToken?: () => string | undefined): string | undefined => {
    const requestUserToken = getRequestUserToken?.();
    return hasValidUserToken(requestUserToken) ? requestUserToken : getValidGoogleAnalyticsUserToken();
};

export const getInitialSearchUserTokenAfterConsent = async (
    getRequestUserToken?: () => string | undefined
): Promise<string | undefined> => {
    await initAlgoliaTracking();

    return waitForInitialSearchUserTokenAfterConsent({
        maxWaitMs: INITIAL_SEARCH_USER_TOKEN_MAX_WAIT_MS,
        pollIntervalMs: GOOGLE_ANALYTICS_TOKEN_POLL_INTERVAL_MS,
        getUserToken: () => getBestInitialSearchUserToken(getRequestUserToken),
    });
};

// Disable Algolia Analytics (`analytics`) and click analytics (`clickAnalytics`)
// for every request in a batch so the query isn't recorded or given a `queryID`.
export const disableAnalytics = <T extends { params?: Record<string, unknown> }>(
    requests: readonly T[]
): readonly T[] =>
    requests.map(request => ({
        ...request,
        params: {
            ...request.params,
            analytics: false,
            clickAnalytics: false,
        },
    }));

export const stripAnalyticsAttribution = <T extends { params?: Record<string, unknown> }>(request: T): T => {
    const { userToken: _userToken, ...paramsWithoutUserToken } = request.params ?? {};

    return {
        ...request,
        params: {
            ...paramsWithoutUserToken,
            analytics: false,
            clickAnalytics: false,
        },
    };
};

const enableAnalyticsAttribution = <T extends { params?: Record<string, unknown> }>(request: T): T => ({
    ...request,
    params: {
        ...request.params,
        analytics: true,
        clickAnalytics: true,
    },
});

export const shouldAttributeRequest = (request: AlgoliaRequestWithParams): boolean =>
    !isOptedOutTracking() && hasValidUserToken(request.params?.userToken);

// When the user has opted out of tracking via OneTrust, or the request cannot
// be attributed to a real user token, scrub the analytics signals. The query
// still runs; it just doesn't produce Algolia analytics.
export const stripAnalyticsWhenUnattributable = <T extends { params?: Record<string, unknown> }>(
    requests: readonly T[]
): readonly T[] =>
    requests.map(request => (shouldAttributeRequest(request) ? request : stripAnalyticsAttribution(request)));

const addUserToken = <T extends AlgoliaRequestWithParams>(request: T, userToken: string | undefined): T => {
    if (!userToken || hasValidUserToken(request.params?.userToken)) {
        return request;
    }

    return {
        ...request,
        params: {
            ...request.params,
            userToken,
        },
    };
};

export const getInitialUrlRefinements = (
    urlFilters: SFCCFilterMap[] | Facets[],
    isSearchPage: boolean
): UrlRefinement[] => {
    const routeState = Object.assign({}, ...(urlFilters as Facets[])) as IAlgoliaRouteState;
    const initialState = getStateFromRoute(routeState, isSearchPage);
    const refinementList = (initialState.refinementList ?? {}) as Record<string, unknown>;

    return Object.entries(refinementList)
        .filter((entry): entry is [string, string[]] => Array.isArray(entry[1]) && entry[1].length > 0)
        .map(([attribute, values]) => ({ attribute, values }));
};

/**
 * Prepares the initial client-side Algolia search requests before the user interacts.
 *
 * URL-derived refinements, when present, are re-applied to every request so the first client search
 * cannot drop them before the user interacts with InstantSearch. The helper also attaches the
 * resolved user token and enables Algolia analytics attribution for only the first attributable
 * initial request; remaining requests are stripped of analytics attribution.
 *
 * @param options - Inputs used to prepare the initial search request list.
 * @param options.searchRequests - Algolia search requests emitted by InstantSearch.
 * @param options.initialUrlRefinements - URL-derived refinements to preserve on the initial request, if any.
 * @param options.userToken - Resolved analytics user token, when consent and token lookup provide one.
 * @param options.hasAlreadyAttributedInitialRequest - Whether an earlier initial request already received attribution.
 * @returns Prepared search requests and whether this call enabled analytics attribution for one request.
 */
export const prepareInitialUrlRefinementRequests = <T extends AlgoliaRequestWithParams>(options: {
    searchRequests: readonly T[];
    initialUrlRefinements: readonly UrlRefinement[];
    userToken?: string;
    hasAlreadyAttributedInitialRequest: boolean;
}): { preparedRequests: readonly T[]; didEnableAnalyticsAttribution: boolean } => {
    const { searchRequests, initialUrlRefinements, userToken, hasAlreadyAttributedInitialRequest } = options;
    let hasAttributedInitialRequestSoFar = hasAlreadyAttributedInitialRequest;
    let didEnableAnalyticsAttribution = false;

    const preparedRequests = searchRequests.map(searchRequest => {
        const urlRefinedRequest = addUrlRefinementsToRequest(searchRequest, initialUrlRefinements);
        const requestWithUserToken = addUserToken(urlRefinedRequest, userToken);
        if (!hasAttributedInitialRequestSoFar && shouldAttributeRequest(requestWithUserToken)) {
            hasAttributedInitialRequestSoFar = true;
            didEnableAnalyticsAttribution = true;
            return enableAnalyticsAttribution(requestWithUserToken);
        }

        return stripAnalyticsAttribution(urlRefinedRequest);
    });

    return {
        preparedRequests,
        didEnableAnalyticsAttribution,
    };
};

const uiStateSatisfiesUrlRefinements = (uiState: UiState, refinements: readonly UrlRefinement[]): boolean => {
    if (!refinements.length) {
        return true;
    }

    const indexUiState = (uiState as IUiState)[AlgoliaIndice.PRODUCTS] ?? Object.values(uiState).at(0);
    const refinementList = (indexUiState?.refinementList ?? {}) as Record<string, unknown>;

    return refinements.every(({ attribute, values }) => {
        const currentValues = refinementList[attribute];
        return Array.isArray(currentValues) && values.every(value => currentValues.includes(value));
    });
};

export const addUrlRefinementsToUiState = (uiState: UiState, refinements: readonly UrlRefinement[]): UiState => {
    if (uiStateSatisfiesUrlRefinements(uiState, refinements)) {
        return uiState;
    }

    const indexName = (uiState as IUiState)[AlgoliaIndice.PRODUCTS]
        ? AlgoliaIndice.PRODUCTS
        : (Object.keys(uiState)[0] ?? AlgoliaIndice.PRODUCTS);
    const indexUiState = uiState[indexName] ?? {};
    const currentRefinementList = indexUiState.refinementList ?? {};
    const refinementList: Record<string, string[]> = { ...currentRefinementList };

    refinements.forEach(({ attribute, values }) => {
        const currentValues = refinementList[attribute];
        const nextValues = Array.isArray(currentValues) ? [...currentValues] : [];
        values.forEach(value => {
            if (!nextValues.includes(value)) {
                nextValues.push(value);
            }
        });
        refinementList[attribute] = nextValues;
    });

    return {
        ...uiState,
        [indexName]: {
            ...indexUiState,
            refinementList,
        },
    };
};
